import { useState, useEffect, useRef, useCallback } from 'react';
import AudioRecorder from 'audio-recorder-polyfill';

import mpegEncoder from 'audio-recorder-polyfill/mpeg-encoder';
import { useStopwatch2 } from './useStopwatch';

AudioRecorder.encoder = mpegEncoder;
AudioRecorder.prototype.mimeType = 'audio/mpeg';
// @ts-ignore
window.MediaRecorder = AudioRecorder;

export enum recordingStatusEnum {
  EMPTY,
  RECORDING,
  PAUSED,
  FINISHED
}

type tUseAudioRecorderReturnType = [
  recordingStatusEnum,
  number | undefined,
  Blob | null,
  {
    startRecording: () => void;
    pauseRecording: () => void;
    finishRecording: () => void;
    resetRecording: () => void;
    getVolume: () => number;
  }
];

type tUseAudioRecorderArgsType = {
  onRecordChange?: (audioUrl: string | null, blob?: Blob | null) => void;
  onError?: (errorMessage: string) => void;
  setDuration?: (seconds: number) => void;
};

export function useAudioRecorder({
  onRecordChange,
  onError,
  setDuration
}: tUseAudioRecorderArgsType): tUseAudioRecorderReturnType {
  const [status, setStatus] = useState(recordingStatusEnum.EMPTY);
  const statusRef = useRef<recordingStatusEnum | null>(recordingStatusEnum.EMPTY);
  const [recorder, setRecorder] = useState<MediaRecorder | null>(null);
  const getVolumeRef = useRef<() => number>(() => 0);
  const [stopwatch, seconds] = useStopwatch2();
  const audioBlob = useRef<Blob | null>(null);
  const chunks = useRef<Blob[]>([]);
  const isInitializingRef = useRef(false);
  // False between unmount and (in StrictMode) the next mount. Async work started
  // before unmount must not touch state or leave a live stream behind.
  const isMountedRef = useRef(true);

  const handleDataAvailable = useCallback((_e: BlobEvent) => {
    chunks.current.push(_e.data);
  }, []);

  const handleRecorderStop = useCallback(
    (_e: any) => {
      // Deliberately untyped: PR #573's design doc lists blob format and upload payload as
      // unchanged, and typing the blob here would change the uploaded multipart
      // Content-Type. The playback bug is fixed engine-side instead, by HowlAudioPlayer's
      // `format` fallback keying off the extensionless `blob:` URL.
      audioBlob.current = new Blob(chunks.current);
      chunks.current = [];
      const url = URL.createObjectURL(audioBlob.current);
      onRecordChange?.(url, audioBlob.current);
      _setStatus(recordingStatusEnum.FINISHED);
      stopwatch.reset();
    },
    [onRecordChange, stopwatch]
  );

  const handleRecordError = useCallback(
    (_e: Event) => {
      onError?.((_e as any).error.name);
    },
    [onError]
  );

  const initRecorder = useCallback(async () => {
    let mRec;
    let stream: MediaStream | undefined;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mRec = new MediaRecorder(stream);

      mRec.addEventListener('dataavailable', handleDataAvailable);
      mRec.addEventListener('stop', handleRecorderStop);
      mRec.addEventListener('error', handleRecordError);
    } catch (err) {
      // Release the mic if getUserMedia succeeded but recorder setup failed —
      // otherwise the device stays "in use" and the next attempt can fail.
      stream?.getTracks().forEach((track) => track.stop());
      if (typeof err === 'string') {
        onError?.(err as string);
      } else {
        onError?.((err as Error).toString());
      }
    }
    stopwatch.reset();
    return mRec || null;
  }, [handleDataAvailable, handleRecordError, handleRecorderStop, onError, stopwatch]);

  function _setStatus(status: recordingStatusEnum) {
    setStatus(status);
    statusRef.current = status;
  }
  function _start(_recorder: MediaRecorder): void {
    switch (_recorder.state) {
      case 'recording':
        break;
      case 'paused':
        _recorder.resume();
        break;
      case 'inactive':
        chunks.current = [];
        _recorder.start();
        break;
      default:
        break;
    }
  }
  const _cleanRecorderListeners = useCallback(
    (recorder: MediaRecorder) => {
      recorder.removeEventListener('dataavailable', handleDataAvailable);
      recorder.removeEventListener('stop', handleRecorderStop);
    },
    [handleDataAvailable, handleRecorderStop]
  );

  // API methods START
  const startRecording = useCallback(async () => {
    let rec = recorder;
    if (!rec) {
      // Guard against a double tap kicking off two getUserMedia/MediaRecorder
      // inits, which would orphan a stream + recorder (only the last one wins).
      if (isInitializingRef.current) return;
      isInitializingRef.current = true;
      try {
        rec = await initRecorder();
        // The component can unmount while getUserMedia is pending. The unmount effect
        // keys on the recorder in state, which was still null, so nothing else will
        // release this stream. (`return` here still runs the finally below.)
        if (!isMountedRef.current) {
          if (rec) {
            _cleanRecorderListeners(rec);
            rec.stream.getTracks().forEach((track) => track.stop());
          }
          return;
        }
        setRecorder(rec);
      } finally {
        isInitializingRef.current = false;
      }
    }
    if (rec) {
      _start(rec);
      _setStatus(recordingStatusEnum.RECORDING);
      stopwatch.start();
    }
  }, [initRecorder, recorder, stopwatch, _cleanRecorderListeners]);

  const pauseRecording = useCallback(() => {
    _setStatus(recordingStatusEnum.PAUSED);
    if (recorder) {
      recorder.pause();
      stopwatch.pause();
    }
  }, [recorder, stopwatch]);

  const finishRecording = useCallback(() => {
    stopwatch.pause();
    setDuration && setDuration(seconds ?? 0);
    _setStatus(recordingStatusEnum.FINISHED);
    if (recorder) {
      recorder.stop();
      recorder.stream.getTracks().forEach((track) => track.stop());
      setRecorder(null);
    }
  }, [recorder, stopwatch, setDuration, seconds]);

  const resetRecording = useCallback(() => {
    stopwatch.pause();
    stopwatch.reset();
    _setStatus(recordingStatusEnum.EMPTY);
    if (recorder) {
      recorder.stop();
      recorder.stream.getTracks().forEach((track) => track.stop());
      _cleanRecorderListeners(recorder);
      setRecorder(null);
    }
  }, [_cleanRecorderListeners, recorder, stopwatch]);

  // API methods END

  // Own effect (empty deps) so the flag only tracks mount/unmount — the recorder
  // cleanup effect below re-runs on every recorder change and must not clear it.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    function _cleanRecorder(recorder: MediaRecorder) {
      if (recorder) {
        recorder.stop();
        recorder.stream.getTracks().forEach((track) => track.stop());
        _cleanRecorderListeners(recorder);
      }
    }

    return () => {
      if (recorder && recorder.state !== 'inactive') {
        _cleanRecorder(recorder);
      }
    };
  }, [recorder, _cleanRecorderListeners]);

  return [
    status,
    seconds,
    audioBlob.current,
    {
      startRecording,
      pauseRecording,
      finishRecording,
      resetRecording,
      getVolume: getVolumeRef.current
    }
  ];
}
